import { Alert, Anchor, Badge, Group, Loader, Stack, Text } from '@mantine/core'
import { IconExternalLink, IconPlugConnectedX } from '@tabler/icons-react'
import { MetaList, SectionCard, StatusBadge } from '@lighthouse/design'
import { useRunCancellations } from '../../hooks/useRunCancellation'
import { NOT_MEASURED, whenLabel } from '../../utils/format'
import { hangfireDashboardUrl, hangfireJobUrl } from '../../utils/hangfireUrl'
import { runOutcomeLook } from '../outcomeFormat'
import {
  CANCEL_FAILED_BRIEF,
  CANCEL_MIGRATION,
  CANCEL_NOT_PROVISIONED,
  cancelStatusBrief,
  cancelStatusColor,
  cancelStatusLabel,
  cancellationCardView,
  requestedText,
  runNowText,
  withheldText,
} from './cancelFormat'
import type { EtlRunCancellation } from '../../types/etlRunCancellation'

/**
 * A run's cancel request: its status, who asked and when, the Hangfire jobs the stop was delivered to, and what
 * became of the run. Read from `GET api/v1/etl/runs/{runId}/cancellations` (any signed-in user; who asked and why are
 * withheld from a non-administrator by the server, and said to be).
 *
 * `live` is whether the run can still be cancelled (running or stale). Only then does the card show a read in
 * progress, "nothing asked", or an unprovisioned table; for a finished run it appears only when a request exists or
 * the read failed — a failure might be hiding one. Loading, failed, not offered, unprovisioned, none and a request
 * each look different, and an unprovisioned table is NOT_MEASURED, never "nobody asked".
 */
export function CancellationCard({ runId, live }: { runId: string; live: boolean }) {
  const query = useRunCancellations(runId, live)
  const view = cancellationCardView(query, live)

  switch (view.kind) {
    case 'hidden':
      return null
    case 'loading':
      return (
        <SectionCard title="Cancellation">
          <Group gap="xs">
            <Loader size="xs" />
            <Text size="xs" c="dimmed">
              Reading this run&apos;s cancel requests…
            </Text>
          </Group>
        </SectionCard>
      )
    case 'failed':
      return (
        <SectionCard title="Cancellation">
          <Alert
            icon={<IconPlugConnectedX size={16} />}
            color="yellow"
            variant="light"
            title="Could not read this run's cancel requests — this says nothing about whether one was made"
          >
            {view.summary && <Text size="xs">{view.summary}</Text>}
          </Alert>
        </SectionCard>
      )
    case 'not-offered':
      return (
        <SectionCard title="Cancellation">
          <Text size="sm" c="dimmed" data-state="cancel-not-offered">
            This API does not serve run cancellation.
          </Text>
        </SectionCard>
      )
    case 'unprovisioned':
      return (
        <SectionCard title="Cancellation">
          <Group gap={6} wrap="nowrap" align="flex-start" data-state="cancel-unprovisioned">
            <Text size="sm" ff="monospace">
              {NOT_MEASURED}
            </Text>
            <Text size="sm" c="dimmed">
              Not measured: {CANCEL_NOT_PROVISIONED} (migration {CANCEL_MIGRATION} not applied).
            </Text>
          </Group>
        </SectionCard>
      )
    case 'none':
      return (
        <SectionCard title="Cancellation">
          <Text size="sm" c="dimmed" data-state="cancel-none">
            No cancel has been requested for this run.
          </Text>
        </SectionCard>
      )
    case 'request':
      return <RequestCard request={view.latest} earlier={view.earlier} />
    default:
      return null
  }
}

function RequestCard({ request, earlier }: { request: EtlRunCancellation; earlier: number }) {
  const outcome = request.runOutcome ? runOutcomeLook(request.runOutcome) : null
  const now = runNowText(request)
  const dashboard = hangfireDashboardUrl()

  return (
    <SectionCard
      title="Cancellation"
      subtitle={earlier > 0 ? `The newest of ${earlier + 1} requests for this run` : 'Requested for this run'}
      action={
        <Badge
          variant="light"
          color={cancelStatusColor(request.status)}
          data-state="cancel-status"
          style={{ textTransform: 'none' }}
        >
          {cancelStatusBrief(request)}
        </Badge>
      }
    >
      <Stack gap="sm">
        {request.statusDetail && request.status !== 'Failed' && (
          <Text size="sm" c="dimmed">
            {request.statusDetail}
          </Text>
        )}
        <MetaList
          items={[
            { label: 'Requested', value: requestedText(request) },
            {
              label: 'Note',
              value: request.noteWithheld ? (
                <Text size="sm" c="dimmed" data-state="cancel-note-withheld">
                  {withheldText(request.note, true)}
                </Text>
              ) : (
                withheldText(request.note, false)
              ),
            },
            { label: 'Stale when requested', value: request.runWasStale ? 'Yes' : 'No' },
            { label: 'Stop delivered', value: whenLabel(request.deliveredAt) },
            { label: 'Delivered to', value: <DeliveredJobs ids={request.deliveredJobIds} /> },
            { label: 'Decided', value: whenLabel(request.decidedAt) },
            { label: 'Run status when decided', value: request.runStatusAtDecision ?? NOT_MEASURED },
            {
              label: 'Run now',
              value: outcome ? (
                outcome.role ? (
                  <StatusBadge status={outcome.role} label={outcome.label} />
                ) : (
                  <Badge color="gray" variant="light" radius="sm">
                    {outcome.label}
                  </Badge>
                )
              ) : (
                NOT_MEASURED
              ),
            },
          ]}
        />
        {now && (
          <Text size="xs" c="dimmed">
            {now}
          </Text>
        )}
        {request.status === 'Failed' && (
          <Alert color="red" variant="light" title={CANCEL_FAILED_BRIEF} data-state="cancel-failed">
            <Stack gap={4}>
              <Text size="sm">{cancelStatusLabel(request)}</Text>
              {dashboard ? (
                <Anchor href={dashboard} target="_blank" rel="noopener noreferrer" size="sm">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    Open the Hangfire dashboard
                    <IconExternalLink size={14} />
                  </span>
                </Anchor>
              ) : (
                <Text size="sm" c="dimmed">
                  Hangfire dashboard (address not configured)
                </Text>
              )}
            </Stack>
          </Alert>
        )}
      </Stack>
    </SectionCard>
  )
}

/** The jobs the stop was delivered to, each linked to its Hangfire page when the dashboard's address is known. */
function DeliveredJobs({ ids }: { ids: string[] }) {
  if (ids.length === 0) return <>{NOT_MEASURED}</>
  return (
    <Group gap={6} wrap="wrap" justify="flex-end">
      {ids.map((id) => {
        const url = hangfireJobUrl(id)
        return url ? (
          <Anchor key={id} href={url} target="_blank" rel="noopener noreferrer" size="sm" ff="monospace">
            {id} <IconExternalLink size={12} style={{ verticalAlign: 'middle' }} />
          </Anchor>
        ) : (
          <Text key={id} span size="sm" ff="monospace">
            {id}
          </Text>
        )
      })}
    </Group>
  )
}
